import { createSlice } from '@reduxjs/toolkit';
import {
  fetchFeatureFlagsThunk,
  fetchFeatureFlagsAdminThunk,
  createFeatureFlagThunk,
  updateFeatureFlagThunk,
  deleteFeatureFlagThunk,
} from './featureFlagsActionThunks';

const initialState = {
  // Public: {name: enabled} map, used app-wide to gate UI/routing. Defaults match the
  // pre-existing hardcoded object so nothing changes before the initial fetch resolves.
  // legacyPricingTiers: gates the old SaaS-tier marketing pricing content
  // (see migration 7b2c4f9a1e6d) -- off by default, matching the seeded row.
  flags: { posts: false, legacyPricingTiers: false },
  loaded: false,

  // Admin: full flag objects for the admin panel's toggle UI.
  adminFlags: [],

  loading: { flags: false, adminFlags: false, mutate: false },
  errors: { flags: null, adminFlags: null, mutate: null },
};

const featureFlagsSlice = createSlice({
  name: 'featureFlags',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // === Public flags map ===
      .addCase(fetchFeatureFlagsThunk.pending, (state) => {
        state.loading.flags = true;
        state.errors.flags = null;
      })
      .addCase(fetchFeatureFlagsThunk.fulfilled, (state, action) => {
        state.flags = { ...state.flags, ...action.payload };
        state.loaded = true;
        state.loading.flags = false;
      })
      .addCase(fetchFeatureFlagsThunk.rejected, (state, action) => {
        state.loading.flags = false;
        state.errors.flags = action.payload || 'Failed to load feature flags';
      })

      // === Admin flag list ===
      .addCase(fetchFeatureFlagsAdminThunk.pending, (state) => {
        state.loading.adminFlags = true;
        state.errors.adminFlags = null;
      })
      .addCase(fetchFeatureFlagsAdminThunk.fulfilled, (state, action) => {
        state.adminFlags = action.payload;
        state.loading.adminFlags = false;
      })
      .addCase(fetchFeatureFlagsAdminThunk.rejected, (state, action) => {
        state.loading.adminFlags = false;
        state.errors.adminFlags = action.payload || 'Failed to load feature flags';
      })

      // === Create ===
      .addCase(createFeatureFlagThunk.pending, (state) => {
        state.loading.mutate = true;
        state.errors.mutate = null;
      })
      .addCase(createFeatureFlagThunk.fulfilled, (state, action) => {
        state.loading.mutate = false;
        state.adminFlags.push(action.payload);
        state.flags[action.payload.name] = action.payload.enabled;
      })
      .addCase(createFeatureFlagThunk.rejected, (state, action) => {
        state.loading.mutate = false;
        state.errors.mutate = action.payload || 'Failed to create feature flag';
      })

      // === Update ===
      .addCase(updateFeatureFlagThunk.pending, (state) => {
        state.loading.mutate = true;
        state.errors.mutate = null;
      })
      .addCase(updateFeatureFlagThunk.fulfilled, (state, action) => {
        state.loading.mutate = false;
        const idx = state.adminFlags.findIndex((f) => f.name === action.payload.name);
        if (idx !== -1) state.adminFlags[idx] = action.payload;
        state.flags[action.payload.name] = action.payload.enabled;
      })
      .addCase(updateFeatureFlagThunk.rejected, (state, action) => {
        state.loading.mutate = false;
        state.errors.mutate = action.payload || 'Failed to update feature flag';
      })

      // === Delete ===
      .addCase(deleteFeatureFlagThunk.pending, (state) => {
        state.loading.mutate = true;
        state.errors.mutate = null;
      })
      .addCase(deleteFeatureFlagThunk.fulfilled, (state, action) => {
        state.loading.mutate = false;
        const name = action.payload;
        state.adminFlags = state.adminFlags.filter((f) => f.name !== name);
        delete state.flags[name];
      })
      .addCase(deleteFeatureFlagThunk.rejected, (state, action) => {
        state.loading.mutate = false;
        state.errors.mutate = action.payload || 'Failed to delete feature flag';
      });
  },
});

export default featureFlagsSlice.reducer;
